import React, { useEffect, useRef, useState } from 'react';
import { getInitials } from '../../utils/formatters';

/* Палитра для «нарисованного» аватара. Цвета попадают в canvas, поэтому
   живут значениями в JS, а не переменными CSS. */
const PALETTE = [
  { name: 'Индиго',  from: '#4f46e5', to: '#7c3aed' },
  { name: 'Океан',   from: '#0ea5e9', to: '#2563eb' },
  { name: 'Изумруд', from: '#10b981', to: '#059669' },
  { name: 'Закат',   from: '#f59e0b', to: '#ef4444' },
  { name: 'Роза',    from: '#ec4899', to: '#8b5cf6' },
  { name: 'Бирюза',  from: '#06b6d4', to: '#0ea5e9' },
  { name: 'Гранат',  from: '#dc2626', to: '#9f1239' },
  { name: 'Графит',  from: '#475569', to: '#1e293b' }
];

const SIZE = 256;

/* Квадрат с градиентом и инициалами — в БД уедет компактный PNG */
const drawInitials = (text, color) => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  const gradient = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  gradient.addColorStop(0, color.from);
  gradient.addColorStop(1, color.to);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, SIZE, SIZE);

  ctx.fillStyle = '#ffffff';
  ctx.font = '600 104px Inter, Segoe UI, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, SIZE / 2, SIZE / 2 + 4);

  return canvas.toDataURL('image/png');
};

/* Фото обрезаем по центру в квадрат и ужимаем: строка едет в БД целиком */
const cropPhoto = (img) => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  const side = Math.min(img.width, img.height);
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;
  ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);

  return canvas.toDataURL('image/jpeg', 0.85);
};

const AvatarEditor = ({ profile, onSave, onDelete, onClose }) => {
  const [mode, setMode] = useState('initials');
  const [initials, setInitials] = useState('');
  const [colorIndex, setColorIndex] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const text = (initials || getInitials(profile?.full_name) || '').substring(0, 2).toUpperCase();
  const color = PALETTE[colorIndex];
  const hasAvatar = Boolean(profile?.avatar);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Нужен файл изображения');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setPhoto(cropPhoto(img));
        setMode('photo');
        setError('');
      };
      img.onerror = () => setError('Не удалось прочитать изображение');
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(mode === 'photo' && photo ? photo : drawInitials(text, color));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось сохранить аватар');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (e) {
      setError('Не удалось удалить аватар');
      setSaving(false);
    }
  };

  return (
    <div className="ui-modal" onClick={onClose} role="presentation">
      <div className="ui-modal-box" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ui-modal-head">
          <h2>Аватар</h2>
          <button className="ui-icon-btn" type="button" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5" />
            </svg>
          </button>
        </div>

        <div className="lk-avatar-preview">
          {mode === 'photo' && photo ? (
            <img src={photo} alt="" />
          ) : (
            <span style={{ backgroundImage: `linear-gradient(135deg, ${color.from}, ${color.to})` }}>{text}</span>
          )}
        </div>

        <div className="ui-segmented" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'initials'}
            className={mode === 'initials' ? 'active' : ''}
            onClick={() => setMode('initials')}
          >
            Инициалы
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'photo'}
            className={mode === 'photo' ? 'active' : ''}
            onClick={() => setMode('photo')}
          >
            Фото
          </button>
        </div>

        {mode === 'initials' ? (
          <>
            <div className="ui-field">
              <label htmlFor="lk-initials">Символы или эмодзи</label>
              <input
                id="lk-initials"
                type="text"
                maxLength="2"
                placeholder={getInitials(profile?.full_name)}
                value={initials}
                onChange={(e) => setInitials(e.target.value)}
              />
            </div>
            <div className="ui-field">
              <span className="ui-field-label">Цвет</span>
              <div className="lk-swatches">
                {PALETTE.map((item, index) => (
                  <button
                    key={item.name}
                    type="button"
                    title={item.name}
                    aria-label={item.name}
                    aria-pressed={index === colorIndex}
                    className={`lk-swatch ${index === colorIndex ? 'active' : ''}`}
                    style={{ backgroundImage: `linear-gradient(135deg, ${item.from}, ${item.to})` }}
                    onClick={() => setColorIndex(index)}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <div
            className={`lk-dropzone ${dragOver ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => fileRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 9.00195C19.175 9.01406 20.3529 9.11051 21.1213 9.8789C22 10.7576 22 12.1718 22 15.0002V16.0002C22 18.8286 22 20.2429 21.1213 21.1215C20.2426 22.0002 18.8284 22.0002 16 22.0002H8C5.17157 22.0002 3.75736 22.0002 2.87868 21.1215C2 20.2429 2 18.8286 2 16.0002L2 15.0002C2 12.1718 2 10.7576 2.87868 9.87889C3.64706 9.11051 4.82497 9.01406 7 9.00195" />
              <path d="M12 15L12 2M9 5.5L12 2L15 5.5" />
            </svg>
            <div className="lk-dropzone-text">
              {photo ? 'Выбрать другое изображение' : 'Перетащите фото или нажмите'}
              <small>Обрежем по центру до 256×256</small>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        )}

        {error && <div className="ui-inline-error">{error}</div>}

        <div className="ui-modal-actions">
          {hasAvatar && (
            <button type="button" className="ui-btn-danger" onClick={handleDelete} disabled={saving}>
              Удалить
            </button>
          )}
          <button type="button" className="ui-btn-ghost" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type="button" className="ui-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarEditor;
